import styled from 'styled-components';
import { color, duration, ease, font, radius, shadow } from '../../../theme/tokens';
import { reducedMotion } from '../../atome/motion/motion.styled';

export const drawerPaperSx = {
  width: { xs: '100%', sm: '25rem' },
  backgroundColor: color.cream,
  backgroundImage: `radial-gradient(80% 40% at 50% 0%, ${color.sandLight} 0%, transparent 70%)`,
  boxShadow: shadow.lift,
};

export const BagHead = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.4rem 1rem;
  border-bottom: 1px solid ${color.lineSoft};

  h2 {
    font-family: ${font.display};
    font-size: 1.6rem;
    font-weight: 600;
    color: ${color.ink};
    margin: 0.5rem 0 0;
  }
`;

export const CloseButton = styled.button`
  display: grid;
  place-items: center;
  width: 2.1rem;
  height: 2.1rem;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: ${color.inkSoft};
  background: transparent;
  transition: background ${duration.fast} ${ease.out}, color ${duration.fast} ${ease.out};

  &:hover {
    background: rgba(255, 255, 255, 0.8);
    color: ${color.forest};
  }
`;

export const BagBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.2rem 1.4rem;
`;

export const Line = styled.div<{ $muted?: boolean }>`
  display: flex;
  gap: 0.9rem;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid ${color.lineSoft};
  opacity: ${({ $muted }) => ($muted ? 0.55 : 1)};

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

export const LineImage = styled.img`
  width: 68px;
  height: 68px;
  flex: 0 0 auto;
  object-fit: cover;
  border-radius: ${radius.md};
  background: ${color.creamDeep};
`;

export const LineBody = styled.div`
  flex: 1;
  min-width: 0;

  h3 {
    font-family: ${font.body};
    font-size: 0.92rem;
    font-weight: 500;
    color: ${color.ink};
    margin: 0 0 0.15rem;
  }

  p {
    font-family: ${font.body};
    font-size: 0.8rem;
    color: ${color.inkSoft};
    margin: 0;
  }

  del {
    margin-left: 0.35rem;
    color: ${color.inkFaint};
  }
`;

export const LineFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  margin-top: 0.55rem;
`;

export const Stepper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  button {
    width: 26px;
    height: 26px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    cursor: pointer;
    color: ${color.forest};
    background: transparent;
    border: 1px solid ${color.sandDeep};
    transition: background ${duration.fast} ${ease.out}, color ${duration.fast} ${ease.out};
  }

  button:hover:not(:disabled) {
    background: ${color.forest};
    border-color: ${color.forest};
    color: #fff;
  }

  button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  span {
    min-width: 1.6rem;
    text-align: center;
    font-family: ${font.body};
    font-size: 0.88rem;
    color: ${color.ink};
  }
`;

export const LinePrice = styled.strong`
  font-family: ${font.body};
  font-size: 0.95rem;
  font-weight: 600;
  color: ${color.forestDeep};
`;

export const RemoveLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.74rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${color.inkFaint};
  transition: color ${duration.fast} ${ease.out};

  &:hover {
    color: ${color.danger};
  }
`;

export const SoldOutTag = styled.span`
  display: inline-block;
  margin-top: 0.35rem;
  padding: 0.15rem 0.5rem;
  border-radius: ${radius.pill};
  font-family: ${font.body};
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  color: ${color.danger};
  background: rgba(180, 84, 74, 0.11);
`;

export const BagFoot = styled.footer`
  padding: 1.2rem 1.4rem 1.6rem;
  border-top: 1px solid ${color.lineSoft};
  background: rgba(255, 255, 255, 0.65);
`;

export const TotalLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 0.9rem;
  font-family: ${font.body};

  span:first-child {
    font-size: 0.9rem;
    color: ${color.inkSoft};
  }

  span:last-child {
    font-family: ${font.display};
    font-size: 1.7rem;
    font-weight: 600;
    color: ${color.forest};
  }
`;

export const SavingNote = styled.p`
  margin: -0.4rem 0 0.9rem;
  font-family: ${font.body};
  font-size: 0.78rem;
  color: ${color.forest};
`;

export const CheckoutCta = styled.button`
  width: 100%;
  padding: 0.95rem 1.5rem;
  border: none;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.05em;
  color: #fff;
  background: ${color.forest};
  box-shadow: 0 16px 34px rgba(74, 112, 97, 0.24);
  transition: transform ${duration.base} ${ease.spring}, background ${duration.base} ${ease.out};

  &:hover:not(:disabled) {
    background: ${color.forestDeep};
    transform: translateY(-2px);
  }

  &:disabled {
    background: ${color.sandDeep};
    cursor: not-allowed;
    box-shadow: none;
  }

  ${reducedMotion};
`;

/** Sits above Checkout — the way back to the shop with the bag kept. */
export const KeepShoppingCta = styled.button`
  width: 100%;
  margin-bottom: 0.6rem;
  padding: 0.85rem 1.5rem;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.9rem;
  letter-spacing: 0.04em;
  color: ${color.forestDeep};
  background: transparent;
  border: 1px solid rgba(74, 112, 97, 0.28);
  transition: background ${duration.base} ${ease.out}, border-color ${duration.base} ${ease.out};

  &:hover {
    background: #fff;
    border-color: ${color.forest};
  }
`;

export const FootNote = styled.p`
  margin: 0.8rem 0 0;
  text-align: center;
  font-family: ${font.body};
  font-size: 0.76rem;
  line-height: 1.5;
  color: ${color.inkSoft};
`;

export const EmptyBag = styled.div`
  padding: 3.5rem 1.5rem;
  text-align: center;

  h3 {
    font-family: ${font.display};
    font-size: 1.5rem;
    color: ${color.ink};
    margin: 1rem 0 0.4rem;
  }

  p {
    font-family: ${font.body};
    font-size: 0.88rem;
    line-height: 1.7;
    color: ${color.inkSoft};
    margin: 0 0 1.6rem;
  }
`;

/** Bag button in the navbar, with its count bubble. */
export const BagButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 1.1rem;
  border-radius: ${radius.pill};
  cursor: pointer;
  font-family: ${font.body};
  font-size: 0.82rem;
  letter-spacing: 0.05em;
  color: ${color.forestDeep};
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(74, 112, 97, 0.26);
  transition: background ${duration.base} ${ease.out}, border-color ${duration.base} ${ease.out};

  &:hover {
    background: #fff;
    border-color: ${color.forest};
  }
`;

export const BagCount = styled.span`
  display: grid;
  place-items: center;
  min-width: 1.15rem;
  height: 1.15rem;
  padding: 0 0.3rem;
  border-radius: ${radius.pill};
  font-size: 0.68rem;
  font-weight: 600;
  color: #fff;
  background: ${color.forest};
`;
